import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import Topbar from "../../PulseSurvey/Topbar";
import B2BDonutChart from "../../../component/common/cards/B2BDonutChart";
import GreenArrow from "../../../assets/images/greenupArrow.svg";
import { Bar } from "react-chartjs-2";
import { formatAmount, getLocalStorageItem } from "../../../utils/helper";
import { CompanyApi } from "../../../api/companyApi";
import Loader from "../../../component/common/Loader";

const WellBankDashboard = () => {
  const navigate = useNavigate();
  const userData =
    getLocalStorageItem("userData") &&
    JSON.parse(getLocalStorageItem("userData"));
  const [loader, setLoader] = useState(true);
  const [wellBankDashboardData, setWellBankDashboardData] = useState({});

  const fetchPeapDashboardData = async () => {
    setLoader(true);
    try {
      CompanyApi.getWellBankDashboard().then((response) => {
        setLoader(false);
        const responseData = response?.data || {};
        if (responseData?.meta?.code === 1) {
          const result = responseData?.data || {};
          setWellBankDashboardData(result);
        }
      });
    } catch (error) {
      setLoader(false);
      console.error("Error fetching peap dashboard:", error);
    }
  };

  const getWeekDateRange = useMemo(() => {
    // Get the current date
    const currentDate = new Date();

    // Get the last 6 days including the current date (7 days in total)
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - (6 - i)); // Adjust for the previous 6 days
      return date;
    });

    // Function to format the day with suffix (st, nd, rd, th)
    const formatDayWithSuffix = (date) => {
      const day = date.getDate();
      const suffix =
        day % 10 === 1 && day !== 11
          ? "st"
          : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
      return `${day}${suffix}`;
    };

    // Get the formatted start and end days
    const formattedStartDate = formatDayWithSuffix(days[0]);
    const formattedEndDate = formatDayWithSuffix(days[6]);

    // Get the month and year for the range (start date will determine the month)
    const monthYear = days[6].toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });

    // Return the formatted date range in "31st - 6th Feb 2025" format
    return `${formattedStartDate} - ${formattedEndDate} ${monthYear}`;
  }, []);

  useEffect(() => {
    fetchPeapDashboardData();
  }, []);

  // const data = wellBankDashboardData?.redemptionTrends?.record?.map((item) => item?.Redeemed)

  const data = useMemo(() => {
    // Get the current date and the last 6 days
    const currentDate = new Date();
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - (6 - i)); // Adjust for the previous 6 days
      return date.toLocaleDateString("en-CA"); // Format as 'YYYY-MM-DD'
    });

    // Map through the last 7 days and get redemption data for each day
    return days.map((day) => {
      const redemptionData =
        wellBankDashboardData?.redemptionTrends?.record?.find(
          (item) => item._id === day
        );
      const redeemed = redemptionData ? redemptionData.Redeemed : 0; // If no data, set to 0
      const weekday = new Date(day).toLocaleString("en-US", {
        weekday: "short",
      }); // Get the weekday
      return { redeemed, weekday, date: day };
    });
  }, [wellBankDashboardData]);

  const dataD = {
    labels: data.map((item) => item.weekday),
    datasets: [
      {
        label: "Weekly Usage",
        data: data.map((item) => item.redeemed),
        backgroundColor: "rgba(74, 87, 218, 1)",
        borderColor: "rgba(74, 87, 218, 1)",
        borderRadius: 3,
        borderWidth: 1,
      },
    ],
  };

  const options2 = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          drawOnChartArea: false,
        },
      },
      y: {
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };
  return (
    <div className="relative lg:mb-[45px]">
      {loader && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>WellBank Dashboard</title>
      </Helmet>
      <Topbar title={"Peap WellBank"} />
      <div className="max-w-[355px] leading-[19px] mb-[2px]">
        Let’s streamline the management, distribution, and purchasing of
        Wellpoints for your people.
      </div>
      <div className="flex flex-wrap justify-end gap-[10px] sm:pt-1 pt-3 mb-[18px]">
        <button
          className="leading-9 py-[2px] px-4 border border-[#0B1D2E33] rounded-full whitespace-nowrap dark:border-white dark:text-white text-shoorah-lightBlack"
          onClick={() =>
            navigate(`/${userData?.slug}/peap-wellbank/manage-wellpoint`)
          }
        >
          Manage WellPoints
        </button>
        <button
          className="leading-9 py-[2px] px-4 text-white  whitespace-nowrap bg-[#1F1F1F] rounded-full"
          onClick={() =>
            navigate(`/${userData?.slug}/peap-wellbank/purchase-wellpoint`)
          }
        >
          Purchase WellPoints
        </button>
      </div>
      <div className="w-full grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="w-full bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-4 pr-6 border-[1.5px] border-shoorah-borderGray rounded-xl lg:rounded-2xl text-shoorah-lightBlack dark:text-shoorah-darkBg">
          <div className="flex items-center justify-between ">
            <p className="text-base font-medium leading-9">Current Balance</p>
            <div className="w-8 h-8 bg-[#D9D9D9] rounded-full"></div>
          </div>
          <h4 className="md:text-[40px] text-[30px] font-medium md:leading-[38px] leading-7">
            {formatAmount(wellBankDashboardData?.currentBalance, true)}
          </h4>
          <span className="text-sm font-medium">
            {formatAmount(wellBankDashboardData?.currentBalance, false)}
          </span>
        </div>
        <div className="w-full bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-4 pr-6 border-[1.5px] border-shoorah-borderGray rounded-xl lg:rounded-2xl text-shoorah-lightBlack dark:text-shoorah-darkBg">
          <div className="flex items-center justify-between">
            <p className="text-base font-medium leading-9">
              Total Ever Assigned
            </p>
            <div className="w-8 h-8 bg-[#D9D9D9] rounded-full"></div>
          </div>
          <h4 className="md:text-[40px] text-[30px] font-medium md:leading-[38px] leading-7">
            {formatAmount(wellBankDashboardData?.totalEverAssigned)}
          </h4>
        </div>
        <div className="w-full bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder p-4 pr-6 border-[1.5px] border-shoorah-borderGray rounded-xl lg:rounded-2xl text-shoorah-lightBlack dark:text-shoorah-darkBg">
          <div className="flex items-center justify-between">
            <p className="text-base font-medium leading-9">All Time Ammount</p>
            <div className="w-8 h-8 bg-[#D9D9D9] rounded-full"></div>
          </div>
          <h4 className="md:text-[40px] text-[30px] font-medium md:leading-[38px] leading-7">
            {formatAmount(wellBankDashboardData?.allTimeAmount)}
          </h4>
        </div>
      </div>
      <div className="mt-[18px] grid lg:grid-cols-1 md:grid-cols-2 lg:gap-5 gap-4 xl:grid-cols-2">
        <div>
          <B2BDonutChart
            title="Team Distribution"
            description="Breakdown of sessions by type and focus."
            chartTitle="Top Team"
            dataSource={wellBankDashboardData?.departmentWiseTransfers}
          />
        </div>
        <div>
          <B2BDonutChart
            title="Redemption Rate"
            description="Understanding user distribution by age"
            chartTitle="Common Status"
            dataSource={wellBankDashboardData?.redemptionRate}
          />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 mt-[18px] overflow-x-hidden sm:overflow-x-auto horizontal-card-scrollbar pb-3">
        <div className="w-2/3 overflow-x-auto horizontal-card-scrollbar ">
          <div className="w-full min-w-[615px] p-6 border-[1.5px] lg:rounded-2xl bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder dark:text-white border-shoorah-borderGray rounded-xl">
            <div className="flex justify-between mb-5">
              <div>
                <div className="text-xl font-medium leading-[32px] text-[#14213D] dark:text-white mb-1">
                  Redemption Trends
                </div>
                <div>{getWeekDateRange}</div>
              </div>
              <div className="text-right">
                <div className="text-xl font-medium leading-[32px] text-[#14213D] dark:text-white mb-1">
                  Total Redeemed:{" "}
                  {formatAmount(
                    wellBankDashboardData?.redemptionTrends?.totalRedeemed
                  )}
                </div>
                <div className="flex items-center justify-end text-base font-medium text-[#28D9A0]">
                  <img src={GreenArrow} alt="GreenArrow" /> +
                  {wellBankDashboardData?.redemptionTrends?.percentage}%
                </div>
              </div>
            </div>
            <div>
              <Bar
                data={dataD}
                options={{
                  ...options2,
                }}
                className="!h-[222px]"
              />
            </div>
          </div>
        </div>
        <div className="min-w-[239px] w-full sm:w-1/3 p-6 border-[1.5px] lg:rounded-2xl flex flex-col justify-between bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder  dark:text-shoorah-darkBg border-shoorah-borderGray rounded-xl">
          <div>
            <h4 className="text-xl mb-[18px]">
              Overall WellPoint Engagement Rate
            </h4>
            <div>
              <select
                id="perPage"
                className={`relative w-full rounded-md max-w-[100px]  cursor-pointer bg-white dark:border-shoorah-darkBorder dark:bg-shoorah-darkBgTabColor dark:text-shoorah-darkBg text-[#1E1E1E] text-left  focus:outline-none sm:text-sm appearance-auto`}
              >
                <option value="10">All Time</option>
                <option value="10">Day</option>
                <option value="20">Month</option>
                <option value="30">Year</option>
              </select>
            </div>
          </div>
          <div>
            <div className="font-medium text-[64px] text-shoorah-lightBlack dark:text-shoorah-darkBg">
              {formatAmount(wellBankDashboardData?.wellPointAngagementRate)}%
            </div>
            <div className="flex items-center text-base font-medium text-[#28D9A0]">
              <img src={GreenArrow} alt="GreenArrow" /> +
              {wellBankDashboardData?.redemptionTrends?.percentage}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellBankDashboard;
